import React, { useEffect, useMemo, useRef, useState } from "react";
import { DAY_META } from "../constants";
import { minutesToDate } from "../dateUtils";
import { formatTimeFromMinutes, minutesFromDate } from "../timeUtils";
import SessionCard from "./SessionCard";
import EventCard from "./EventCard";
import ReactDOM from "react-dom";
import { BiSolidEdit } from "react-icons/bi";
import { computeEventLanes } from "../eventUtils";
import "./EventModal.css";

const SNAP_MINUTES = 5;
const MIN_PX_PER_MINUTE = 2.15;
const MAX_CONCURRENT = 3;
const LANE_HEIGHT = 64;
const LANE_GAP = 8;
const ROW_PADDING = 12;
const CANCEL_MARKER_BAND_HEIGHT = 22;
let activeDragOffsetPx = 0;

function toDateKeyLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const da = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${da}`;
}

function minutesOf(value) {
  if (value == null) return 0;
  if (typeof value === "number") return value;
  return minutesFromDate(new Date(value));
}

function overlaps(aStart, aEnd, bStart, bEnd) {
  return aStart < bEnd && bStart < aEnd;
}

function normalizeStatus(status) {
  const s = (status || "Pending").toLowerCase();
  if (s === "cancelled" || s === "canceled") return "cancelled";
  if (s === "confirmed") return "confirmed";
  if (s === "present") return "present";
  return "pending";
}

function isCancelledStatus(status) {
  return normalizeStatus(status) === "cancelled";
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function snapMinutes(value, step = SNAP_MINUTES) {
  return Math.round(value / step) * step;
}

function formatHourLabel(minute) {
  const h24 = Math.floor(minute / 60) % 24;
  const ampm = h24 >= 12 ? "PM" : "AM";
  const h12 = ((h24 + 11) % 12) + 1;
  return `${h12} ${ampm}`;
}

function mergeSegments(segments) {
  if (!segments.length) return [];
  const sorted = [...segments]
    .filter((s) => Number.isFinite(s.startMin) && Number.isFinite(s.endMin) && s.endMin > s.startMin)
    .sort((a, b) => a.startMin - b.startMin);

  if (!sorted.length) return [];

  const merged = [sorted[0]];
  for (let i = 1; i < sorted.length; i += 1) {
    const current = sorted[i];
    const last = merged[merged.length - 1];
    if (current.startMin <= last.endMin) {
      last.endMin = Math.max(last.endMin, current.endMin);
    } else {
      merged.push({ ...current });
    }
  }
  return merged;
}

function buildAvailabilitySegments(availabilities = [], blocks = {}, timeBlocks = []) {
  const ranges = [];

  if (Array.isArray(availabilities) && availabilities.length) {
    for (const a of availabilities) {
      if (Number.isFinite(a?.startMin) && Number.isFinite(a?.endMin)) {
        ranges.push({ startMin: a.startMin, endMin: a.endMin });
      } else if (a?.start && a?.end) {
        const startMin = minutesOf(a.start);
        const endMin = minutesOf(a.end);
        if (endMin > startMin) {
          ranges.push({ startMin, endMin });
        }
      }
    }
  }

  if (ranges.length) return mergeSegments(ranges);

  for (const block of timeBlocks) {
    const cell = blocks?.[block.id];
    if (cell?.isAvailable) {
      ranges.push({ startMin: block.startMin, endMin: block.endMin });
    }
  }

  return mergeSegments(ranges);
}

function collectSessionsFromBlocks(blocks = {}, timeBlocks = []) {
  const output = [];
  const seen = new Set();

  for (const block of timeBlocks) {
    const lists = blocks?.[block.id]?.sessionLists || [];

    for (const slotSessions of lists) {
      for (const session of slotSessions || []) {
        const key = session?.id || `${session?.studentId || "student"}|${minutesOf(session?.start)}|${minutesOf(session?.end)}|${session?.slotIdx ?? 0}`;
        if (seen.has(key)) continue;
        seen.add(key);
        output.push(session);
      }
    }
  }

  return output;
}

function computeLaneLayout(sessions = []) {
  const visualOrderOf = (session) => {
    const raw = Number(session?.visualOrder);
    if (Number.isFinite(raw)) return raw;
    if (Number.isInteger(session?.slotIdx)) return session.slotIdx;
    return 0;
  };

  const sorted = [...sessions].sort((a, b) => {
    const startDiff = minutesOf(a.start) - minutesOf(b.start);
    if (startDiff !== 0) return startDiff;
    const orderDiff = visualOrderOf(a) - visualOrderOf(b);
    if (orderDiff !== 0) return orderDiff;
    return minutesOf(a.end) - minutesOf(b.end);
  });

  const laneEnds = Array(MAX_CONCURRENT).fill(-Infinity);

  return sorted.map((session) => {
    const startMin = minutesOf(session.start);
    const endMin = Math.max(startMin + SNAP_MINUTES, minutesOf(session.end));

    let lane = null;
    for (let idx = 0; idx < MAX_CONCURRENT; idx += 1) {
      if (laneEnds[idx] <= startMin) {
        lane = idx;
        break;
      }
    }

    if (lane == null) {
      lane = laneEnds.indexOf(Math.min(...laneEnds));
    }

    laneEnds[lane] = Math.max(laneEnds[lane], endMin);

    return {
      ...session,
      startMin,
      endMin,
      lane,
    };
  });
}

function findContainingBlock(timeBlocks, minute) {
  if (!Array.isArray(timeBlocks) || timeBlocks.length === 0) return null;

  const exact = timeBlocks.find((b) => minute >= b.startMin && minute < b.endMin);
  if (exact) return exact;

  let nearest = timeBlocks[0];
  let bestDistance = Number.POSITIVE_INFINITY;

  for (const block of timeBlocks) {
    const distance =
      minute < block.startMin
        ? block.startMin - minute
        : minute > block.endMin
          ? minute - block.endMin
          : 0;

    if (distance < bestDistance) {
      bestDistance = distance;
      nearest = block;
    }
  }

  return nearest;
}

function pickAvailableLane(positionedSessions, startMin, endMin, excludeId = null) {
  const entries = (positionedSessions || [])
    .filter((session) => {
      if (excludeId && session.id === excludeId) return false;
      if (isCancelledStatus(session.status)) return false;
      return overlaps(session.startMin, session.endMin, startMin, endMin);
    })
    .map((session, index) => ({
      id: session.id || `session_${index}`,
      startMin: session.startMin,
      endMin: session.endMin,
    }));

  entries.push({
    id: "__candidate__",
    startMin,
    endMin,
  });

  entries.sort((a, b) => {
    if (a.startMin !== b.startMin) return a.startMin - b.startMin;
    if (a.endMin !== b.endMin) return a.endMin - b.endMin;
    if (a.id === "__candidate__") return 1;
    if (b.id === "__candidate__") return -1;
    return 0;
  });

  const laneEnds = Array(MAX_CONCURRENT).fill(-Infinity);
  const laneById = new Map();

  for (const entry of entries) {
    let lane = null;
    for (let idx = 0; idx < MAX_CONCURRENT; idx += 1) {
      if (laneEnds[idx] <= entry.startMin) {
        lane = idx;
        break;
      }
    }

    if (lane == null) {
      if (entry.id === "__candidate__") return null;
      lane = laneEnds.indexOf(Math.min(...laneEnds));
    }

    laneEnds[lane] = Math.max(laneEnds[lane], entry.endMin);
    laneById.set(entry.id, lane);
  }

  return laneById.get("__candidate__") ?? null;
}

function maxConcurrentWithinRange(positionedSessions, startMin, endMin, excludeId = null) {
  const events = [];

  for (const session of positionedSessions || []) {
    if (excludeId && session.id === excludeId) continue;
    if (isCancelledStatus(session.status)) continue;

    const sessionStart = Number.isFinite(session.startMin) ? session.startMin : minutesOf(session.start);
    const sessionEnd = Number.isFinite(session.endMin)
      ? session.endMin
      : Math.max(sessionStart + SNAP_MINUTES, minutesOf(session.end));

    if (!overlaps(sessionStart, sessionEnd, startMin, endMin)) continue;

    const overlapStart = Math.max(sessionStart, startMin);
    const overlapEnd = Math.min(sessionEnd, endMin);
    if (overlapEnd <= overlapStart) continue;

    events.push({ minute: overlapStart, delta: 1 });
    events.push({ minute: overlapEnd, delta: -1 });
  }

  events.sort((a, b) => {
    if (a.minute !== b.minute) return a.minute - b.minute;
    return a.delta - b.delta;
  });

  let active = 0;
  let peak = 0;
  for (const event of events) {
    active += event.delta;
    if (active > peak) peak = active;
  }
  return peak;
}

function buildStudentLabel(session) {
  if (session?.studentLabel) return session.studentLabel;
  if (session?.studentName && session?.studentGrade != null) {
    return `${session.studentName} (${session.studentGrade})`;
  }
  return session?.studentName || "Student";
}

function buildCancelledMarkers(cancelledSessions = []) {
  const grouped = new Map();

  for (const session of cancelledSessions) {
    const startMin = minutesOf(session.start);
    const endMin = Math.max(startMin + SNAP_MINUTES, minutesOf(session.end));
    const key = `${startMin}|${endMin}`;

    if (!grouped.has(key)) {
      grouped.set(key, {
        key,
        startMin,
        endMin,
        sessions: [],
      });
    }
    grouped.get(key).sessions.push(session);
  }

  return [...grouped.values()].sort((a, b) => a.startMin - b.startMin);
}

function normalizeClockIntervals(clockIntervals = []) {
  return [...(clockIntervals || [])]
    .map((interval, idx) => {
      const startMin = Number.isFinite(interval?.startMin) ? interval.startMin : minutesOf(interval?.start);
      const rawEndMin = Number.isFinite(interval?.endMin) ? interval.endMin : minutesOf(interval?.end);
      const endMin = Math.max(startMin + 1, rawEndMin);
      if (!Number.isFinite(startMin) || !Number.isFinite(endMin) || endMin <= startMin) return null;

      return {
        ...interval,
        key: interval?.id || `${idx}-${startMin}-${endMin}`,
        startMin,
        endMin,
        isOpen: !!interval?.isOpen,
      };
    })
    .filter(Boolean)
    .sort((a, b) => a.startMin - b.startMin);
}

function buildClockBadgeLabel(interval) {
  const startLabel = formatTimeFromMinutes(Math.round(interval.startMin));
  const endLabel = formatTimeFromMinutes(Math.round(interval.endMin));
  return interval.isOpen ? `In ${startLabel}` : `${startLabel} - ${endLabel}`;
}

function buildClockTooltip(interval) {
  const startLabel = formatTimeFromMinutes(Math.round(interval.startMin));
  const endLabel = formatTimeFromMinutes(Math.round(interval.endMin));
  if (interval.isOpen) {
    return `Clock In: ${startLabel} (live)`;
  }
  return `Clock In: ${startLabel} | Clock Out: ${endLabel}`;
}

export default function DaySection({
  dayKey,
  date,
  timeBlocks,
  timelineStartMin,
  timelineEndMin,
  tutorRows,
  dayRef,
  isClosed,
  onEditDay,
  onBulkPresentDay,
  confirmedCount = 0,
  onEmptySlotDoubleClick,
  onSessionDoubleClick,
  onChangeSessionStatus,
  selectedSessionId,
  selectedSlotKey,
  onEmptySlotClick,
  onSessionClick,
  onSlotDrop,
  onSessionDragStart,
  onSessionResize,
  onSessionReorder,
  onSecretClockEdit,
  headerDayOnly = false,
  readOnly,
  isTutorView = false,
  sessionNotificationsBySessionId = {},
  sessionLogBySessionId = {},
  showSessionLogIndicator = false,
  showMissingSessionLogIndicator = false,
  onOpenSessionLog,
  showCancelledMarkers = true,
  sessionLogIndicatorInteractive = true,
  sessionLogSubmittedTooltip = "Session log submitted",
  sessionLogMissingTooltip = "Session log missing",
  sessionAnimationById = {},
  // Events row props
  events = [],
  onEventClick,
  onEventDoubleClick,
  onEventTrackDoubleClick,
  onEventDrop,
  selectedEventId = null,
}) {
  const meta = DAY_META[dayKey] || { label: dayKey };
  const dayDateKey = useMemo(() => toDateKeyLocal(date), [date]);

  const dateLabel = useMemo(() => {
    if (headerDayOnly) return meta.label;
    return `${meta.label}, ${date.toLocaleDateString()}`;
  }, [headerDayOnly, meta.label, date]);

  const closedDateLabel = useMemo(
    () => date.toLocaleDateString([], { weekday: "long", month: "long", day: "numeric", year: "numeric" }),
    [date]
  );

  const rows = useMemo(() => {
    return tutorRows.map(({ tutor, blocks = {}, isGhost, availabilities = [], clockIntervals = [] }) => {
      const availabilitySegments = buildAvailabilitySegments(availabilities, blocks, timeBlocks);
      const rawSessions = collectSessionsFromBlocks(blocks, timeBlocks).map((s) => ({
        ...s,
        studentLabel: buildStudentLabel(s),
      }));
      const normalizedClockIntervals = normalizeClockIntervals(clockIntervals);

      const activeSessions = rawSessions.filter((s) => !isCancelledStatus(s.status));
      const cancelledSessions = rawSessions.filter((s) => isCancelledStatus(s.status));
      const positionedSessions = computeLaneLayout(activeSessions);
      const cancelledMarkers = showCancelledMarkers ? buildCancelledMarkers(cancelledSessions) : [];
      // Dynamic lane count: show occupied lanes + 1 empty lane for easy session creation,
      // capped at MAX_CONCURRENT = 3. Minimum 1 so empty rows are still clickable.
      // Examples: 0 sessions → 1 lane; 1 session → 2 lanes; 2 concurrent → 3 lanes; 3 → 3 (max).
      // This is a VIEW-ONLY optimisation — scheduling rules (MAX_CONCURRENT = 3) are unchanged.
      const peakLane = positionedSessions.reduce((max, s) => Math.max(max, s.lane + 1), 0);
      // Tutor view: always show the full MAX_CONCURRENT = 3 rails (fixed, predictable layout).
      // Admin view: dynamic — occupied lanes + 1 empty, capped at MAX_CONCURRENT.
      const laneCount = isTutorView
        ? MAX_CONCURRENT
        : Math.min(MAX_CONCURRENT, Math.max(1, peakLane + 1));
      const markerBandHeight = cancelledMarkers.length ? CANCEL_MARKER_BAND_HEIGHT : 0;
      const rowHeight = Math.max(
        84,
        markerBandHeight + ROW_PADDING * 2 + laneCount * LANE_HEIGHT + Math.max(0, laneCount - 1) * LANE_GAP
      );

      return {
        tutor,
        isGhost,
        availabilitySegments,
        clockIntervals: normalizedClockIntervals,
        positionedSessions,
        cancelledSessions,
        cancelledMarkers,
        markerBandHeight,
        rowHeight,
      };
    });
  }, [tutorRows, timeBlocks, showCancelledMarkers, isTutorView]);

  // Compute lane layout for the events row (no MAX_CONCURRENT cap)
  // ↓ Adjust EVENT_LANE_HEIGHT to change the height of each event card lane
  const EVENT_LANE_HEIGHT = 40;
  // ↓ Adjust EVENT_LANE_GAP to change the vertical gap between overlapping event lanes
  const EVENT_LANE_GAP = 3;
  // ↓ Adjust EVENT_ROW_PADDING to change the top/bottom padding of the events row
  const EVENT_ROW_PADDING = 5;

  const positionedEvents = useMemo(() => computeEventLanes(events), [events]);

  const occupiedLaneCount = useMemo(
    () => positionedEvents.reduce((max, e) => Math.max(max, e.lane + 1), 0),
    [positionedEvents]
  );

  // Past days (readOnly): show only the lanes that actually have events (no extra empty lane).
  // Current/future days: always show occupied + 1 extra empty lane for easy event creation.
  // When eventLaneCount === 0 the events row is hidden entirely (see render below).
  // Tutor view: never show the events row (events are admin-only).
  const eventLaneCount = isTutorView
    ? 0  // Events row is hidden in tutor view
    : readOnly
      ? occupiedLaneCount              // past: exact match, 0 → row hidden
      : (occupiedLaneCount === 0 ? 1 : occupiedLaneCount + 1); // future: +1 empty

  const eventRowHeight = useMemo(
    () => EVENT_ROW_PADDING * 2 + eventLaneCount * EVENT_LANE_HEIGHT + Math.max(0, eventLaneCount - 1) * EVENT_LANE_GAP,
    [eventLaneCount, EVENT_LANE_HEIGHT, EVENT_LANE_GAP, EVENT_ROW_PADDING]
  );

  const configuredBounds = useMemo(() => {
    const start = Number(timelineStartMin);
    const end = Number(timelineEndMin);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end <= start) return null;

    const snappedStart = Math.floor(start / SNAP_MINUTES) * SNAP_MINUTES;
    const snappedEnd = Math.ceil(end / SNAP_MINUTES) * SNAP_MINUTES;
    return {
      startMin: snappedStart,
      endMin: Math.max(snappedStart + 60, snappedEnd),
    };
  }, [timelineStartMin, timelineEndMin]);

  const bounds = useMemo(() => {
    if (configuredBounds) return configuredBounds;

    let rawStart = Number.POSITIVE_INFINITY;
    let rawEnd = Number.NEGATIVE_INFINITY;

    for (const block of timeBlocks) {
      rawStart = Math.min(rawStart, block.startMin);
      rawEnd = Math.max(rawEnd, block.endMin);
    }

    for (const row of rows) {
      for (const seg of row.availabilitySegments) {
        rawStart = Math.min(rawStart, seg.startMin);
        rawEnd = Math.max(rawEnd, seg.endMin);
      }

      for (const session of row.positionedSessions) {
        rawStart = Math.min(rawStart, session.startMin);
        rawEnd = Math.max(rawEnd, session.endMin);
      }

      for (const marker of row.cancelledMarkers || []) {
        rawStart = Math.min(rawStart, marker.startMin);
        rawEnd = Math.max(rawEnd, marker.endMin);
      }
    }

    // Include event times in bounds so the grid expands to show them
    for (const event of positionedEvents) {
      if (Number.isFinite(event.startTime)) rawStart = Math.min(rawStart, event.startTime);
      if (Number.isFinite(event.endTime)) rawEnd = Math.max(rawEnd, event.endTime);
    }

    if (!Number.isFinite(rawStart) || !Number.isFinite(rawEnd) || rawEnd <= rawStart) {
      rawStart = 9 * 60;
      rawEnd = 17 * 60;
    }

    const startMin = Math.floor(rawStart / 60) * 60;
    const endMin = Math.max(startMin + 60, Math.ceil(rawEnd / 60) * 60);

    return { startMin, endMin };
  }, [configuredBounds, rows, timeBlocks, positionedEvents]);

  const timelineWidth = useMemo(
    () => Math.max(720, (bounds.endMin - bounds.startMin) * MIN_PX_PER_MINUTE),
    [bounds.endMin, bounds.startMin]
  );
  const totalMinutes = useMemo(
    () => Math.max(SNAP_MINUTES, bounds.endMin - bounds.startMin),
    [bounds.endMin, bounds.startMin]
  );

  const hourMarks = useMemo(() => {
    const values = [];
    for (let minute = bounds.startMin; minute <= bounds.endMin; minute += 60) {
      values.push(minute);
    }
    return values;
  }, [bounds.endMin, bounds.startMin]);

  const [hoverState, setHoverState] = useState({ tutorUid: null, minute: null });
  const [cancelPopover, setCancelPopover] = useState(null);
  const [resizingPreview, setResizingPreview] = useState(null);
  const [nowTimestamp, setNowTimestamp] = useState(() => Date.now());
  const cancelPopoverRef = useRef(null);
  const resizeStateRef = useRef(null);

  useEffect(() => {
    setHoverState({ tutorUid: null, minute: null });
    setCancelPopover(null);
  }, [dayKey, dayDateKey, bounds.startMin, bounds.endMin]);

  useEffect(() => {
    const timerId = window.setInterval(() => {
      setNowTimestamp(Date.now());
    }, 15000);

    return () => window.clearInterval(timerId);
  }, []);

  useEffect(() => {
    if (!cancelPopover) return undefined;

    const handleOutsideClick = (event) => {
      if (cancelPopoverRef.current?.contains(event.target)) return;
      if (event.target?.closest?.(".sessionStatusMenu")) return;
      setCancelPopover(null);
    };

    document.addEventListener("mousedown", handleOutsideClick);
    window.addEventListener("scroll", handleOutsideClick, true);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
      window.removeEventListener("scroll", handleOutsideClick, true);
    };
  }, [cancelPopover]);

  useEffect(() => {
    if (!showCancelledMarkers) {
      setCancelPopover(null);
    }
  }, [showCancelledMarkers]);

  useEffect(() => {
    return () => {
      const activeResize = resizeStateRef.current;
      if (!activeResize) return;
      document.removeEventListener("mousemove", activeResize.handleMouseMove);
      document.removeEventListener("mouseup", activeResize.handleMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      resizeStateRef.current = null;
    };
  }, []);

  const selectedSlotInfo = useMemo(() => {
    if (!selectedSlotKey) return null;
    const parts = String(selectedSlotKey).split("|");
    if (parts.length < 3) return null;
    const minute = Number(parts[2]);
    if (!Number.isFinite(minute)) return null;
    return {
      dateKey: parts[0],
      tutorUid: parts[1],
      minute,
    };
  }, [selectedSlotKey]);

  const minuteToLeft = (minute) => ((minute - bounds.startMin) / totalMinutes) * timelineWidth;

  const nowMinute = useMemo(() => {
    const now = new Date(nowTimestamp);
    return now.getHours() * 60 + now.getMinutes() + now.getSeconds() / 60;
  }, [nowTimestamp]);

  const showNowIndicator = useMemo(() => {
    if (isClosed) return false;
    const todayKey = toDateKeyLocal(new Date(nowTimestamp));
    if (todayKey !== dayDateKey) return false;
    return nowMinute >= bounds.startMin && nowMinute <= bounds.endMin;
  }, [isClosed, nowTimestamp, dayDateKey, nowMinute, bounds.startMin, bounds.endMin]);

  const getSnappedMinuteFromClientX = (clientX, canvasElement) => {
    const rect = canvasElement.getBoundingClientRect();
    const x = clamp(clientX - rect.left, 0, rect.width || 0);
    const ratio = rect.width > 0 ? x / rect.width : 0;
    const raw = bounds.startMin + ratio * totalMinutes;
    return clamp(snapMinutes(raw), bounds.startMin, bounds.endMin);
  };

  const getSnappedMinuteFromEvent = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const x = clamp(event.clientX - rect.left, 0, rect.width || 0);
    const ratio = rect.width > 0 ? x / rect.width : 0;
    const raw = bounds.startMin + ratio * totalMinutes;
    return clamp(snapMinutes(raw), bounds.startMin, bounds.endMin);
  };

  const getDragAlignedMinuteFromEvent = (event) => {
    const cursorMinute = getSnappedMinuteFromEvent(event);
    let rawOffsetPx = Number(event.dataTransfer.getData("application/x-sched-card-offset-px"));
    
    // Fallback for onDragOver where the browser hides dataTransfer data
    if (!rawOffsetPx && activeDragOffsetPx) {
      rawOffsetPx = activeDragOffsetPx;
    }
    
    if (!Number.isFinite(rawOffsetPx)) return cursorMinute;

    const rect = event.currentTarget.getBoundingClientRect();
    const offsetRatio = rect.width > 0 ? rawOffsetPx / rect.width : 0;
    const offsetMinutes = offsetRatio * totalMinutes;
    const alignedMinute = cursorMinute - offsetMinutes;
    return clamp(snapMinutes(alignedMinute), bounds.startMin, bounds.endMin);
  };

  const isMinuteAvailableForRow = (row, minute) =>
    row.availabilitySegments.some((seg) => minute >= seg.startMin && minute < seg.endMin);

  const isRangeAvailableForRow = (row, startMin, endMin) =>
    row.availabilitySegments.some((seg) => startMin >= seg.startMin && endMin <= seg.endMin);

  const canResizeSessionInRow = (row, sessionId, startMin, endMin) => {
    if (endMin <= startMin) return false;
    if (!isRangeAvailableForRow(row, startMin, endMin)) return false;
    const existingPeak = maxConcurrentWithinRange(row.positionedSessions, startMin, endMin, sessionId);
    return existingPeak < MAX_CONCURRENT;
  };

  const finishResizeSession = async (activeResize) => {
    document.removeEventListener("mousemove", activeResize.handleMouseMove);
    document.removeEventListener("mouseup", activeResize.handleMouseUp);
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    resizeStateRef.current = null;

    const changed =
      activeResize.currentStartMin !== activeResize.originalStartMin ||
      activeResize.currentEndMin !== activeResize.originalEndMin;

    setResizingPreview(null);
    setHoverState({ tutorUid: null, minute: null });
    if (!changed || !onSessionResize) return;

    const nextBlock = findContainingBlock(timeBlocks, activeResize.currentStartMin);

    try {
      await onSessionResize(activeResize.session.id, {
        date,
        dayKey,
        tutorUid: activeResize.row.tutor.id,
        tutorName: activeResize.row.tutor.name || activeResize.row.tutor.email || "Tutor",
        blockId: nextBlock?.id || activeResize.session.blockId || "",
        startMin: activeResize.currentStartMin,
        endMin: activeResize.currentEndMin,
      });
    } catch (error) {
      console.error("Failed to resize session", error);
    }
  };

  const startResizeSession = (event, row, session, edge) => {
    if (readOnly || isTutorView || row.isGhost) return;
    if (resizeStateRef.current) return;

    event.preventDefault();
    event.stopPropagation();

    const canvasElement = event.currentTarget.closest(".timelineTrackCanvas");
    if (!canvasElement) return;

    const initialStartMin = clamp(session.startMin, bounds.startMin, bounds.endMin - SNAP_MINUTES);
    const initialEndMin = clamp(session.endMin, initialStartMin + SNAP_MINUTES, bounds.endMin);

    const activeResize = {
      row,
      session,
      edge,
      canvasElement,
      originalStartMin: initialStartMin,
      originalEndMin: initialEndMin,
      currentStartMin: initialStartMin,
      currentEndMin: initialEndMin,
      handleMouseMove: null,
      handleMouseUp: null,
    };

    const applyCandidateFromClientX = (clientX) => {
      const snappedMinute = getSnappedMinuteFromClientX(clientX, canvasElement);
      let nextStartMin = activeResize.originalStartMin;
      let nextEndMin = activeResize.originalEndMin;

      if (edge === "start") {
        nextStartMin = clamp(
          snappedMinute,
          bounds.startMin,
          activeResize.originalEndMin - SNAP_MINUTES
        );
      } else {
        nextEndMin = clamp(
          snappedMinute,
          activeResize.originalStartMin + SNAP_MINUTES,
          bounds.endMin
        );
      }

      if (!canResizeSessionInRow(row, session.id, nextStartMin, nextEndMin)) return;
      if (
        nextStartMin === activeResize.currentStartMin &&
        nextEndMin === activeResize.currentEndMin
      ) {
        return;
      }

      activeResize.currentStartMin = nextStartMin;
      activeResize.currentEndMin = nextEndMin;
      setHoverState({
        tutorUid: row.tutor.id,
        minute: edge === "start" ? nextStartMin : nextEndMin,
      });
      setResizingPreview({
        sessionId: session.id,
        startMin: nextStartMin,
        endMin: nextEndMin,
      });
    };

    activeResize.handleMouseMove = (moveEvent) => {
      moveEvent.preventDefault();
      applyCandidateFromClientX(moveEvent.clientX);
    };

    activeResize.handleMouseUp = (upEvent) => {
      upEvent.preventDefault();
      const finishedResize = resizeStateRef.current;
      if (!finishedResize) return;
      void finishResizeSession(finishedResize);
    };

    resizeStateRef.current = activeResize;
    setHoverState({
      tutorUid: row.tutor.id,
      minute: edge === "start" ? initialStartMin : initialEndMin,
    });
    setResizingPreview({
      sessionId: session.id,
      startMin: initialStartMin,
      endMin: initialEndMin,
    });

    document.addEventListener("mousemove", activeResize.handleMouseMove);
    document.addEventListener("mouseup", activeResize.handleMouseUp);
    document.body.style.cursor = "ew-resize";
    document.body.style.userSelect = "none";
  };

  const buildSlotContext = (row, clickedMinute, durationMin = 120, excludeSessionId = null) => {
    if (row.isGhost) return { reason: "unavailable" };

    const segment = row.availabilitySegments.find(
      (seg) => clickedMinute >= seg.startMin && clickedMinute < seg.endMin
    );

    if (!segment) return { reason: "unavailable" };

    const startMin = clamp(clickedMinute, segment.startMin, segment.endMin - SNAP_MINUTES);
    const defaultEndMin = Math.min(startMin + durationMin, segment.endMin);

    if (defaultEndMin <= startMin) return { reason: "unavailable" };

    // --- FIX: Gradually shrink the duration if the 2-hour window is blocked by a future session ---
    let lane = null;
    let validEndMin = defaultEndMin;

    while (validEndMin > startMin) {
      lane = pickAvailableLane(row.positionedSessions, startMin, validEndMin, excludeSessionId);
      if (lane != null) break;
      validEndMin -= SNAP_MINUTES; // Shrink the check window by 5 minutes and try again
    }

    if (lane == null) return { reason: "full" };

    const fallbackBlock = {
      id: `custom_${dayKey}_${segment.startMin}_${segment.endMin}`,
      label: `${formatTimeFromMinutes(segment.startMin)} - ${formatTimeFromMinutes(segment.endMin)}`,
      startMin: segment.startMin,
      endMin: segment.endMin,
    };

    const block = findContainingBlock(timeBlocks, startMin) || fallbackBlock;

    return {
      slotKey: `${dayDateKey}|${row.tutor.id}|${startMin}`,
      date,
      dayKey,
      tutorUid: row.tutor.id,
      tutorName: row.tutor.name || row.tutor.email || "Tutor",
      block,
      slotIdx: lane,
      startMin,
      defaultEndMin: validEndMin, 
      availabilityStartMin: segment.startMin,
      availabilityEndMin: segment.endMin,
      maxEndMin: segment.endMin,
      timeBlocks,
      existingSessions: row.positionedSessions.map((s) => ({
        id: s.id,
        start: s.start,
        end: s.end,
        status: s.status,
        slotIdx: Number.isInteger(s.lane) ? s.lane : s.slotIdx ?? 0,
      })),
    };
  };

  const handleTrackClick = (event, row) => {
    if (readOnly || isTutorView) return;
    if (resizeStateRef.current) return;
    if (
      event.target.closest(".sessionCard") ||
      event.target.closest(".sessionStatusMenu") ||
      event.target.closest(".timelineCancelledMarker") ||
      event.target.closest(".timelineCancelledPopover")
    ) return;

    const minute = getSnappedMinuteFromEvent(event);
    if (!isMinuteAvailableForRow(row, minute)) {
      setHoverState({ tutorUid: null, minute: null });
      return;
    }
    
    setHoverState({ tutorUid: row.tutor.id, minute });
    const slot = buildSlotContext(row, minute, 110);

    if (slot.reason === "unavailable") return;
    if (slot.reason === "full") {
      alert("This tutor already has 3 concurrent students for that time range.");
      return;
    }

    // ONLY trigger the selection (single click)
    onEmptySlotClick?.(slot);
  };

  const handleTrackDoubleClick = (event, row) => {
    if (readOnly || isTutorView) return;
    if (resizeStateRef.current) return;
    if (
      event.target.closest(".sessionCard") ||
      event.target.closest(".sessionStatusMenu") ||
      event.target.closest(".timelineCancelledMarker") ||
      event.target.closest(".timelineCancelledPopover")
    ) return;

    const minute = getSnappedMinuteFromEvent(event);
    if (!isMinuteAvailableForRow(row, minute)) return;

    const slot = buildSlotContext(row, minute, 110);
    if (slot.reason === "unavailable" || slot.reason === "full") return;

    // ONLY trigger the modal (double click)
    onEmptySlotDoubleClick?.(slot);
  };

  const handleTrackDrop = (event, row) => {
    if (readOnly || isTutorView) return;
    if (resizeStateRef.current) return;

    event.preventDefault();
    event.stopPropagation();

    const draggedSessionId = event.dataTransfer.getData("text/plain");
    if (!draggedSessionId) return;

    const minute = getDragAlignedMinuteFromEvent(event);
    if (!isMinuteAvailableForRow(row, minute)) return;
    const slot = buildSlotContext(row, minute, SNAP_MINUTES, draggedSessionId);

    if (slot.reason === "unavailable" || slot.reason === "full") return;

    onSlotDrop?.(draggedSessionId, slot);
  };

  const canReorderDropTarget = (row, draggedSessionId, targetSession) => {
    if (!draggedSessionId || draggedSessionId === targetSession.id) return false;

    const dragged = row.positionedSessions.find((s) => s.id === draggedSessionId);
    if (!dragged) return false;
    if (isCancelledStatus(dragged.status) || isCancelledStatus(targetSession.status)) return false;

    return overlaps(dragged.startMin, dragged.endMin, targetSession.startMin, targetSession.endMin);
  };

  const openCancelledPopover = (event, row, marker) => {
    event.preventDefault();
    event.stopPropagation();

    const rect = event.currentTarget.getBoundingClientRect();
    const preferredTop = rect.bottom + 8;
    const fallbackTop = rect.top - 8;
    const viewportBottom = window.innerHeight || document.documentElement.clientHeight;

    let top = preferredTop;
    if (preferredTop + 220 > viewportBottom) {
      top = Math.max(10, fallbackTop - 220);
    }

    // 1. Grab the exact zoom scale from the main schedule wrapper
    const rawZoom = document.querySelector(".schedZoomOuter")?.style.getPropertyValue("--sched-zoom");
    const currentZoom = parseFloat(rawZoom) || 1;

    setCancelPopover({
      tutorUid: row.tutor.id,
      markerKey: marker.key,
      sessions: marker.sessions,
      top,
      left: clamp(rect.left, 10, (window.innerWidth || document.documentElement.clientWidth) - 320),
      zoom: currentZoom /* <--- 2. Save the zoom level to state */
    });
  };

  const handleSecretClockEditIntent = (event, row, interval) => {
    if (!onSecretClockEdit || readOnly || isTutorView) return;
    if (!event.altKey) return;
    event.preventDefault();
    event.stopPropagation();
    onSecretClockEdit({ row, tutor: row.tutor, interval, dayKey, date });
  };

  return (
    <div
      className={`daySection daySection--timeline ${dayKey === "saturday" ? "daySection--sat" : "daySection--weekday"}`}
      ref={dayRef}
      style={{ "--timeline-width": `${timelineWidth}px` }}
    >
      <div className="dayHeader">
        <div className="dayHeaderLeft">{dateLabel}</div>

        <div className="dayHeaderActions">
          {!isTutorView && onEditDay && (
            <button
              className="dayEditIconBtn" 
              title="Edit Day"
              onClick={(e) => {
                e.stopPropagation();
                onEditDay(dayKey, date);
              }}
            >
              <BiSolidEdit size={20} />
            </button>
          )}
        </div>
      </div>

      {isClosed ? (
        <div className="timelineDayBody">
          <div className="timelineClosedBanner">CLOSED: {closedDateLabel}</div>
        </div>
      ) : (
        <div className="timelineDayBody">
          <div className="timelineScroll">
            <div className="timelineGrid">
              <div className="timelineAxisRow">
                <div className="timelineTutorHeaderCell" />
                <div
                  className="timelineAxisTrack"
                  style={{ width: "100%" }}
                >
                  {showNowIndicator && (
                    <div className="timelineNowLine" style={{ left: minuteToLeft(nowMinute) }} />
                  )}

                  {hourMarks.map((minute) => {
                    const left = minuteToLeft(minute);
                    return (
                      <div key={minute} className="timelineAxisHour" style={{ left }}>
                        <div className="timelineHourLine" />
                        <div className="timelineHourLabel">{formatHourLabel(minute)}</div>
                      </div>
                    );
                  })}

                  {/* Hover line + time label in the axis header */}
                  {hoverState.minute != null && (
                    <>
                      <div className="timelineHoverLine" style={{ left: minuteToLeft(hoverState.minute) }} />
                      <div
                        className="timelineHoverTimeLabel"
                        style={{ left: minuteToLeft(hoverState.minute) }}
                      >
                        {formatTimeFromMinutes(hoverState.minute)}
                      </div>
                    </>
                  )}
                </div>
              </div>

              {/* ── Events Row — hidden on past days with no events ── */}
              {eventLaneCount > 0 && (
              <div className="timelineTutorRow timelineEventRow">
                {/* Empty label cell — keeps layout aligned with tutor name cells */}
                <div className="timelineTutorCell" />

                <div className="timelineTrackViewport">
                  <div
                    className="timelineTrackCanvas timelineEventCanvas"
                    style={{ width: "100%", height: eventRowHeight }}
                    onMouseMove={(e) => {
                      if (resizeStateRef.current) return;
                      const minute = getSnappedMinuteFromEvent(e);
                      // Always set minute for global cursor; tutorUid only when interactive
                      setHoverState({ tutorUid: readOnly ? null : "__events__", minute });
                    }}
                    onMouseLeave={() => {
                      if (resizeStateRef.current) return;
                      setHoverState({ tutorUid: null, minute: null });
                    }}
                    onDoubleClick={(e) => {
                      if (readOnly) return;
                      if (e.target.closest(".eventCard")) return;
                      const minute = getSnappedMinuteFromEvent(e);
                      const defaultEnd = Math.min(minute + 60, bounds.endMin);
                      onEventTrackDoubleClick?.({ date, dayKey: dayDateKey, startMin: minute, endMin: defaultEnd });
                    }}
                    onDragOver={(e) => {
                      if (readOnly) return;
                      // Only accept drags that originated from an event card
                      if (!e.dataTransfer.types.includes("application/x-event-card")) return;
                      e.preventDefault();
                      const minute = getDragAlignedMinuteFromEvent(e);
                      setHoverState({ tutorUid: "__events__", minute });
                    }}
                    onDrop={(e) => {
                      setHoverState({ tutorUid: null, minute: null });
                      if (readOnly) return;
                      if (e.dataTransfer.getData("application/x-event-card") !== "true") return;
                      e.preventDefault();
                      e.stopPropagation();
                      const droppedEventId = e.dataTransfer.getData("text/plain");
                      if (!droppedEventId) return;
                      const newStartMin = getDragAlignedMinuteFromEvent(e);
                      const original = positionedEvents.find((ev) => ev.id === droppedEventId);
                      if (!original) return;
                      const duration = Math.max(5, original.endTime - original.startTime);
                      const newStart = clamp(newStartMin, bounds.startMin, bounds.endMin - 5);
                      const newEnd = Math.min(newStart + duration, bounds.endMin);
                      onEventDrop?.(droppedEventId, { startTime: newStart, endTime: newEnd });
                    }}
                  >
                    {showNowIndicator && (
                      <div className="timelineNowLine" style={{ left: minuteToLeft(nowMinute) }} />
                    )}

                    {/* Hover line inside events canvas — placed before cards so cards render on top */}
                    {hoverState.minute != null && (
                      <div className="timelineGlobalHoverLine" style={{ left: minuteToLeft(hoverState.minute) }} />
                    )}

                    {positionedEvents.map((event) => {
                      const startMin = clamp(event.startTime, bounds.startMin, bounds.endMin);
                      const endMin   = clamp(event.endTime, bounds.startMin, bounds.endMin);
                      if (endMin <= startMin) return null;
                      const left  = minuteToLeft(startMin);
                      const width = Math.max(48, minuteToLeft(endMin) - minuteToLeft(startMin) - 3);
                      const lane  = Number.isInteger(event.lane) ? event.lane : 0;
                      const top   = EVENT_ROW_PADDING + lane * (EVENT_LANE_HEIGHT + EVENT_LANE_GAP);
                      return (
                        <div
                          key={event.id}
                          style={{ position: "absolute", left, top, width, height: EVENT_LANE_HEIGHT, zIndex: 5 }}
                          draggable={!readOnly}
                          onDragStart={(e) => {
                            if (resizeStateRef.current) { e.preventDefault(); return; }
                            e.dataTransfer.setData("text/plain", event.id);
                            e.dataTransfer.setData("application/x-event-card", "true");
                            const cardRect = e.currentTarget.getBoundingClientRect();
                            const offsetPx = clamp(e.clientX - cardRect.left, 0, cardRect.width || 0);
                            e.dataTransfer.setData("application/x-sched-card-offset-px", String(offsetPx));
                            activeDragOffsetPx = offsetPx;
                          }}
                        >
                          <EventCard
                            event={event}
                            isSelected={selectedEventId === event.id}
                            onClick={onEventClick}
                            onDoubleClick={onEventDoubleClick}
                          />
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              )} {/* end eventLaneCount > 0 */}

              {rows.length === 0 && !readOnly ? (
                <div className="timelineNoTutors">No tutors available.</div>
              ) : (
                rows.map((row) => {
                  const showSelectedLine =
                    selectedSlotInfo &&
                    selectedSlotInfo.dateKey === dayDateKey &&
                    selectedSlotInfo.tutorUid === row.tutor.id;

                  return (
                    <div key={row.tutor.id} className={`timelineTutorRow ${row.isGhost ? "timelineTutorRow--ghost" : ""}`}>
                      <div className="timelineTutorCell">
                        <div>
                          <div className="timelineTutorName">{row.tutor.name || row.tutor.email || "Tutor"}</div>
                          {row.clockIntervals.length > 0 && (
                            <div className="timelineTutorClockMeta">
                              {row.clockIntervals.slice(0, 2).map((interval) => (
                                <span
                                  key={`clock-badge-${row.tutor.id}-${interval.key}`}
                                  className={`timelineClockBadge ${interval.isOpen ? "timelineClockBadge--open" : ""}`}
                                  title={buildClockTooltip(interval)}
                                  onClick={(event) => handleSecretClockEditIntent(event, row, interval)}
                                >
                                  {buildClockBadgeLabel(interval)}
                                </span>
                              ))}
                              {row.clockIntervals.length > 2 && (
                                <span className="timelineClockBadge timelineClockBadge--more">
                                  +{row.clockIntervals.length - 2} more
                                </span>
                              )}
                            </div>
                          )}
                          {row.isGhost && <div className="timelineTutorWarning">Unavailable. Move sessions.</div>}
                        </div>
                      </div>

                      <div className="timelineTrackViewport">
                        <div
                          className="timelineTrackCanvas"
                          style={{ width: "100%", height: row.rowHeight }}
                          onMouseMove={(e) => {
                            if (resizeStateRef.current) return;
                            const minute = getSnappedMinuteFromEvent(e);
                            // Always set minute so the global cursor shows over unavailable areas too.
                            // Only set tutorUid when the slot is available (for session-creation interaction).
                            const available = isMinuteAvailableForRow(row, minute) && !readOnly && !isTutorView;
                            setHoverState({ tutorUid: available ? row.tutor.id : null, minute });
                          }}
                          onMouseLeave={() => {
                            if (resizeStateRef.current) return;
                            setHoverState({ tutorUid: null, minute: null });
                          }}
                          onClick={(e) => handleTrackClick(e, row)}
                          onDoubleClick={(e) => handleTrackDoubleClick(e, row)}
                          onDragOver={(e) => {
                            if (readOnly || isTutorView) return;
                            if (resizeStateRef.current) return;
                            e.preventDefault();
                            
                            // Calculate the left edge of the dragged card and update the hover line
                            const minute = getDragAlignedMinuteFromEvent(e);
                            if (isMinuteAvailableForRow(row, minute)) {
                              setHoverState({ tutorUid: row.tutor.id, minute });
                            } else {
                              setHoverState({ tutorUid: null, minute: null });
                            }
                          }}
                          onDrop={(e) => {
                            setHoverState({ tutorUid: null, minute: null }); /* Clear the line on drop */
                            handleTrackDrop(e, row);
                          }}
                        >
                          {showNowIndicator && (
                            <div className="timelineNowLine" style={{ left: minuteToLeft(nowMinute) }} />
                          )}

                          {row.availabilitySegments.map((segment, idx) => {
                            const left = minuteToLeft(segment.startMin);
                            const width = Math.max(
                              2,
                              minuteToLeft(segment.endMin) - minuteToLeft(segment.startMin)
                            );

                            return (
                              <div
                                key={`${row.tutor.id}-avail-${idx}`}
                                className="timelineAvailabilityBlock"
                                style={{ left, width }}
                              />
                            );
                          })}

                          {row.clockIntervals.map((interval) => {
                            const startMin = clamp(interval.startMin, bounds.startMin, bounds.endMin);
                            
                            // Override the database end time with the live 'nowMinute' if they are clocked in today
                            let rawEndMin = interval.endMin;
                            const todayKey = toDateKeyLocal(new Date(nowTimestamp));
                            if (interval.isOpen && dayDateKey === todayKey) {
                              rawEndMin = Math.max(startMin + 1, nowMinute);
                            }
                            
                            const endMin = clamp(rawEndMin, bounds.startMin, bounds.endMin);
                            if (endMin <= startMin) return null;

                            const leftStart = minuteToLeft(startMin);
                            const leftEnd = minuteToLeft(endMin);
                            const width = Math.max(2, leftEnd - leftStart);
                            const tooltip = buildClockTooltip(interval);

                            return (
                              <React.Fragment key={`clock-line-${row.tutor.id}-${interval.key}`}>
                                <div
                                  className={`timelineClockSpan ${interval.isOpen ? "timelineClockSpan--open" : ""}`}
                                  style={{ left: leftStart, width }}
                                />
                                <div
                                  className="timelineClockLine timelineClockLine--in"
                                  style={{ left: leftStart }}
                                  title={`Clock In: ${formatTimeFromMinutes(Math.round(startMin))}`}
                                  onClick={(event) => handleSecretClockEditIntent(event, row, interval)}
                                />
                                <div
                                  className={`timelineClockLine ${interval.isOpen ? "timelineClockLine--live" : "timelineClockLine--out"}`}
                                  style={{ left: leftEnd }}
                                  title={tooltip}
                                  onClick={(event) => handleSecretClockEditIntent(event, row, interval)}
                                />
                              </React.Fragment>
                            );
                          })}

                          {row.cancelledMarkers.map((marker) => {
                            const left = minuteToLeft(marker.startMin);
                            const width = Math.max(
                              14,
                              minuteToLeft(marker.endMin) - minuteToLeft(marker.startMin)
                            );
                            const markerBandTop = Math.max(2, ROW_PADDING - 9);
                            const markerButtonTop = markerBandTop + 4;
                            const label =
                              marker.sessions.length > 1
                                ? `${marker.sessions.length} Canceled`
                                : "1 Canceled";;

                            return (
                              <React.Fragment key={`cancel-marker-${row.tutor.id}-${marker.key}`}>
                                <div
                                  className="timelineCancelledGhost"
                                  style={{ left, width, top: markerBandTop }}
                                  aria-hidden="true"
                                />
                                <button
                                  type="button"
                                  className="timelineCancelledMarker"
                                  style={{ left: left + 3, top: markerButtonTop }}
                                  onMouseDown={(event) => event.stopPropagation()}
                                  onClick={(event) => openCancelledPopover(event, row, marker)}
                                  onContextMenu={(event) => openCancelledPopover(event, row, marker)}
                                  title={label}
                                >
                                  {label}
                                </button>
                              </React.Fragment>
                            );
                          })}

                          {showSelectedLine && (
                            <div
                              className="timelineSelectedLine"
                              style={{ left: minuteToLeft(clamp(selectedSlotInfo.minute, bounds.startMin, bounds.endMin)) }}
                            />
                          )}

                          {/* Hover line inside tutor canvas — placed before cards so cards render on top */}
                          {hoverState.minute != null && (
                            <div className="timelineGlobalHoverLine" style={{ left: minuteToLeft(hoverState.minute) }} />
                          )}

                          {row.positionedSessions.map((session) => {
                            const preview =
                              resizingPreview?.sessionId === session.id ? resizingPreview : null;
                            const startMin = clamp(
                              preview?.startMin ?? session.startMin,
                              bounds.startMin,
                              bounds.endMin
                            );
                            const endMin = clamp(
                              preview?.endMin ?? session.endMin,
                              bounds.startMin,
                              bounds.endMin
                            );
                            if (endMin <= startMin) return null;

                            const left = minuteToLeft(startMin);
                            const width = Math.max(56, minuteToLeft(endMin) - minuteToLeft(startMin) - 4);
                            const lane = Number.isInteger(session.lane) ? session.lane : 0;
                            const top = ROW_PADDING + row.markerBandHeight + lane * (LANE_HEIGHT + LANE_GAP);
                            const renderedSession = preview
                              ? {
                                  ...session,
                                  start: minutesToDate(date, startMin),
                                  end: minutesToDate(date, endMin),
                                }
                              : session;

                            return (
                              <div
                                key={session.id || `${session.studentId}-${session.startMin}-${session.endMin}`}
                                className={`timelineSessionWrapper ${preview ? "timelineSessionWrapper--resizing" : ""}`}
                                style={{ left, top, width, height: LANE_HEIGHT }}
                                onDragOver={(event) => {
                                  if (readOnly || isTutorView || row.isGhost || resizeStateRef.current) return;
                                  if (!onSessionReorder) return;
                                  const draggedSessionId = event.dataTransfer.getData("text/plain") || selectedSessionId;
                                  if (!canReorderDropTarget(row, draggedSessionId, session)) return;
                                  event.preventDefault();
                                  event.dataTransfer.dropEffect = "move";
                                }}
                                onDrop={(event) => {
                                  if (readOnly || isTutorView || row.isGhost || resizeStateRef.current) return;
                                  if (!onSessionReorder) return;
                                  const draggedSessionId = event.dataTransfer.getData("text/plain") || selectedSessionId;
                                  if (!canReorderDropTarget(row, draggedSessionId, session)) return;

                                  event.preventDefault();
                                  event.stopPropagation();
                                  void onSessionReorder(draggedSessionId, session.id, {
                                    date,
                                    dayKey,
                                    tutorUid: row.tutor.id,
                                  });
                                }}
                              >
                                {!readOnly && !isTutorView && !row.isGhost && (
                                  <>
                                    <div
                                      className="timelineResizeHandle timelineResizeHandle--left"
                                      onMouseDown={(event) => startResizeSession(event, row, session, "start")}
                                      onClick={(event) => event.stopPropagation()}
                                      onDoubleClick={(event) => event.stopPropagation()}
                                    />
                                    <div
                                      className="timelineResizeHandle timelineResizeHandle--right"
                                      onMouseDown={(event) => startResizeSession(event, row, session, "end")}
                                      onClick={(event) => event.stopPropagation()}
                                      onDoubleClick={(event) => event.stopPropagation()}
                                    />
                                  </>
                                )}
                                <SessionCard
                                  session={renderedSession}
                                  studentLabel={session.studentLabel}
                                  notifications={sessionNotificationsBySessionId?.[session.id] || []}
                                  hasSessionLog={!!sessionLogBySessionId?.[session.id]}
                                  showSessionLogIndicator={showSessionLogIndicator}
                                  showMissingSessionLogIndicator={showMissingSessionLogIndicator}
                                  isPastSession={(new Date(session?.end)).getTime() < Date.now()}
                                  onOpenSessionLog={onOpenSessionLog}
                                  sessionLogIndicatorInteractive={sessionLogIndicatorInteractive}
                                  sessionLogSubmittedTooltip={sessionLogSubmittedTooltip}
                                  sessionLogMissingTooltip={sessionLogMissingTooltip}
                                  animationPhase={sessionAnimationById?.[session.id] || ""}
                                  isSelected={selectedSessionId === session.id}
                                  onDragStart={(event, draggedSession) => {
                                    if (resizeStateRef.current) {
                                      event.preventDefault();
                                      return;
                                    }
                                    const cardRect = event.currentTarget?.getBoundingClientRect?.();
                                    if (cardRect) {
                                      const offsetPx = clamp(event.clientX - cardRect.left, 0, cardRect.width || 0);
                                      event.dataTransfer.setData("application/x-sched-card-offset-px", String(offsetPx));
                                      activeDragOffsetPx = offsetPx;
                                    }
                                    onSessionDragStart?.(event, draggedSession);
                                  }}
                                  onClick={onSessionClick}
                                  onDoubleClick={() => onSessionDoubleClick?.(session)}
                                  onChangeStatus={
                                    onChangeSessionStatus
                                      ? (nextStatus) => onChangeSessionStatus(session.id, nextStatus)
                                      : null
                                  }
                                  isLocked={false}
                                  readOnly={readOnly || isTutorView}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      {cancelPopover && ReactDOM.createPortal(
        <div
          ref={cancelPopoverRef}
          className="timelineCancelledPopover daySection--timeline"
          style={{ 
            /* Divide coordinates by zoom so it doesn't float away, and apply the scale */
            top: cancelPopover.top / cancelPopover.zoom, 
            left: cancelPopover.left / cancelPopover.zoom,
            zoom: cancelPopover.zoom 
          }}
          onMouseDown={(event) => event.stopPropagation()}
        >
          <div className="timelineCancelledPopoverTitle">
            Canceled Sessions ({cancelPopover.sessions.length})
          </div>
          <div className="timelineCancelledPopoverList">
            {cancelPopover.sessions.map((session) => (
              <div key={session.id || `${session.studentId}|${session.start}|${session.end}`} className="timelineCancelledPopoverSession">
                <SessionCard
                  session={session}
                  studentLabel={session.studentLabel || buildStudentLabel(session)}
                  notifications={sessionNotificationsBySessionId?.[session.id] || []}
                  hasSessionLog={!!sessionLogBySessionId?.[session.id]}
                  showSessionLogIndicator={showSessionLogIndicator}
                  showMissingSessionLogIndicator={false}
                  isPastSession={false}
                  onOpenSessionLog={onOpenSessionLog}
                  sessionLogIndicatorInteractive={sessionLogIndicatorInteractive}
                  sessionLogSubmittedTooltip={sessionLogSubmittedTooltip}
                  sessionLogMissingTooltip={sessionLogMissingTooltip}
                  animationPhase={sessionAnimationById?.[session.id] || ""}
                  isSelected={selectedSessionId === session.id}
                  onClick={onSessionClick}
                  onDoubleClick={() => {
                    onSessionDoubleClick?.(session);
                    setCancelPopover(null);
                  }}
                  onChangeStatus={
                    onChangeSessionStatus
                      ? async (nextStatus) => {
                          if (session?.id != null) {
                            const didUpdate = await onChangeSessionStatus(session.id, nextStatus);
                            if (didUpdate === false) return;
                          }
                          setCancelPopover(null);
                        }
                      : null
                  }
                  isLocked={false}
                  readOnly={readOnly || isTutorView}
                />
              </div>
            ))}
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
